import Head from 'next/head'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Modal from 'components/Modal'
import RoundedButton from 'components/RoundedButton'
import FlaredArrow from 'components/SVG/FlaredArrow'
import Section from 'components/Section'
import Spacer from 'components/Spacer'

import { useCloseHeaderModal, useWindowSize } from 'services/hooks'

import CommissionRulesSection from './CommissionRules'
import HowItWorksSection from './HowItWorks'
import SavingsPromoSection from './SavingsPromo'
import SmartBuyersSection from './SmartBuyers'
import SmartSavingsSection from './SmartSavings'

const PageContainer = styled.div`
  margin: 0 auto;
  max-width: ${props => props.theme.metrics.contentMaxWidth}px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 0 15px;
  }
`

const Container = styled.div`
  margin: 0 ${props => props.theme.metrics.gutterPhone}px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    margin: 0;
  }
`

const H2 = styled.h2`
  font-weight: 500;
  font-size: 1.4em;
  line-height: 1.5em;
  text-align: center;
`
const Subhead = styled.h3`
  font-weight: 500;
  font-size: 1.2em;
  line-height: 1.5em;
  text-align: center;
`

const Disclaimer = styled.p`
  font-size: 0.6em;
  line-height: 1.2em;
  text-align: center;
  margin: 20px auto;
  font-style: italic;
`

const MaxWidthContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 0 auto;
`

interface IImgSrcProps {
  imgSrc: string
  mobileImgSrc: string
}

const HeroImageContainer = styled.div<IImgSrcProps>`
  background: center/cover no-repeat url(${props => props.mobileImgSrc});
  width: 100%;
  height: 540px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    background-image: url(${props => props.imgSrc});
  }
`

const HeroContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: ${props => props.theme.colors.transparentBlackAlpha35};
`

const HeroTitle = styled.h1`
  color: ${props => props.theme.colors.grey16};
  font-weight: 800;
  font-size: 32px;
  text-align: center;
  padding: 0;
  margin: 0 10%;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 42px;
    margin: 0 15%;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 56px;
  }
`

const HeroSubtitle = styled.p`
  color: ${props => props.theme.colors.grey16};
  font-weight: 100;
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  padding: 0;
  margin: 0 10%;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 20px;
    margin: 0 15%;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 24px;
    line-height: 32px;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.2em;
  cursor: pointer;
  color: ${props => props.theme.colors.darkBlue};
`

const buyPage: FunctionComponent = () => {
  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()

  useCloseHeaderModal()

  const [isModalVisible, setModalVisible] = useState(false)

  useEffect(() => {
    if (isModalVisible) {
      const script = document.createElement('script')
      script.src = '//js.hsforms.net/forms/embed/v2.js'
      script.async = true
      script.onload = () => {
        if (typeof (window as any).hbspt === 'object') {
          ;(window as any).hbspt.forms.create({
            portalId: '20519632',
            formId: '47a1200d-6419-4978-8358-bbc43b33564c',
            target: '#hubspot-form',
          })
        }
      }

      document.body.appendChild(script)
    }
  }, [isModalVisible])

  const renderFlaredArrowIcon = () => {
    if (width && width >= theme.metrics.tablet) {
      return (
        <FlaredArrow
          fill={theme.colors.white}
          width={28}
          height={28}
        />
      )
    }

    return null
  }

  return (
    <>
      <Head>
        <title>Buy Your Home with HomeLister | Expert Buyer Representation</title>
        <meta
          name="description"
          content="Let HomeLister represent you in your home search. Our expert agents help you find and purchase your dream home while providing professional guidance throughout the buying process."
        />
        <meta
          name="keywords"
          content="Buy Home, Buyer Representation, Home Search, Real Estate Agent, House Hunting"
        />
        <meta
          name="og:description"
          property="og:description"
          content="Let HomeLister represent you in your home search. Our expert agents help you find and purchase your dream home while providing professional guidance throughout the buying process."
        />
      </Head>
      <Section noPadding>
        <HeroImageContainer
          imgSrc={`${process.env.CDN_BASE_URL}/q_auto/amazon/assets/1920/homelister-sell-home-online.jpg`}
          mobileImgSrc={`${process.env.CDN_BASE_URL}/q_auto,w_1440/amazon/assets/1920/homelister-sell-home-online.jpg`}
        >
          <HeroContentContainer>
            <HeroTitle>Buy your next property and get thousands back at closing.</HeroTitle>
            <Spacer size={10} />
            <HeroSubtitle>
              Our network of top agents share their commission with you - giving you thousands back
              at closing to offset today’s high rates, strengthen your down payment, or boost your
              ROI.*
            </HeroSubtitle>
            <Spacer
              size={50}
              tabletSize={40}
              responsiveSize={30}
            />
            <MaxWidthContainer data-testid="getStartedBuy">
              <RoundedButton
                text="Get Started"
                onClick={() => setModalVisible(true)}
                fontSize={16}
                height={60}
                padding="0 40px"
                color={theme.colors.white}
                background={theme.colors.blue4}
                renderRightIcon={renderFlaredArrowIcon}
              />
            </MaxWidthContainer>
          </HeroContentContainer>
        </HeroImageContainer>
      </Section>
      <SmartBuyersSection />
      <HowItWorksSection />
      <SmartSavingsSection />
      <SavingsPromoSection onOpenModal={() => setModalVisible(true)} />
      <CommissionRulesSection />
      <PageContainer>
        <Spacer size={30} />
        <Container>
          <H2>Maximize your property purchase ROI</H2>
          <Subhead>Schedule a quick call to learn your potential rebate amount.</Subhead>
        </Container>
        <MaxWidthContainer>
          <RoundedButton
            text="Get Started"
            onClick={() => setModalVisible(true)}
            fontSize={16}
            height={60}
            padding="0 40px"
            color={theme.colors.white}
            background={theme.colors.blue4}
            renderRightIcon={renderFlaredArrowIcon}
          />
        </MaxWidthContainer>
        <MaxWidthContainer>
          <Disclaimer>
            *Buyer rebate availability and amount varies by state, county, and property type. The
            final rebate value may be affected by state regulations, local real estate commission
            rules, and lender guidelines. Some lenders may restrict how rebates can be applied to
            your purchase. Rebates cannot be combined with other offers and may be subject to
            minimum commission amounts. Contact HomeLister for specific details about rebate
            availability in your area and transaction.
          </Disclaimer>
        </MaxWidthContainer>
        <Spacer size={30} />
      </PageContainer>
      <Modal
        padding="50px 20px"
        setVisible={setModalVisible}
        visible={isModalVisible}
        fixedOverlay
        margin="10vh 5vw"
      >
        <CloseButton onClick={() => setModalVisible(false)}>Close X</CloseButton>
        <div id="hubspot-form" />
      </Modal>
    </>
  )
}

export default buyPage
